import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import AgentTargetsTab from "../components/AgentTargetsTab";
import {
  Plus,
  Loader2,
  UserPlus,
  Shield,
  User,
  Trash2,
  Mail,
  Phone,
  KeyRound,
  Globe,
  Building2,
  Edit2
} from "lucide-react";

const EXPORT_COUNTRIES = [
  "Bangladesh",
  "Vietnam", 
  "Sri Lanka",
  "Indonesia",
  "Thailand",
  "Cambodia",
  "Myanmar",
  "Pakistan",
  "Turkey",
  "Egypt"
];

const Users = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const openEditDialog = (user) => {
    setEditingUser({ ...user });
    setShowEditDialog(true);
  };

  const handleEditUser = async () => {
    if (!editingUser) return;
    try {
      await axios.patch(`${API}/users/${editingUser.id}`, {
        name: editingUser.name,
        phone: editingUser.phone,
        role: editingUser.role,
        crm_type: editingUser.crm_type,
        parent_admin_id: editingUser.parent_admin_id || null,
        joining_date: editingUser.joining_date || null,
        fabric_categories: editingUser.fabric_categories || [],
        fabric_types: editingUser.fabric_types || [],
        regions: editingUser.regions || [],
      });
      toast.success(`${editingUser.name} updated`);
      setShowEditDialog(false);
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update user");
    }
  };

  const [selectedUserForReset, setSelectedUserForReset] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    role: "user",
    crm_type: "domestic",
    export_countries: [],
    parent_admin_id: currentUser?.id || "",
    joining_date: "",
    fabric_categories: [],
    fabric_types: [],
    regions: []
  });

  const adminUsers = users.filter(u => u.role === 'admin');

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      toast.error("Failed to load users");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/auth/register`, newUser);
      toast.success("User created successfully");
      setShowAddDialog(false);
      setNewUser({
        name: "",
        email: "",
        phone: "",
        password: "",
        role: "user",
        crm_type: "domestic",
        export_countries: [],
        parent_admin_id: currentUser?.id || "",
        joining_date: "",
        fabric_categories: [],
        fabric_types: [],
        regions: []
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleActive = async (userId, isActive) => {
    try {
      await axios.patch(`${API}/users/${userId}`, { is_active: !isActive });
      toast.success(isActive ? "User deactivated" : "User activated");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  const handleToggleAvailability = async (userId, isAvailable) => {
    try {
      await axios.patch(`${API}/users/${userId}/availability?is_available=${!isAvailable}`);
      toast.success(isAvailable ? "User marked as off-duty" : "User marked as available");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update user availability");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success("User deleted");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const openResetPasswordDialog = (user) => {
    setSelectedUserForReset(user);
    setNewPassword("");
    setShowResetPasswordDialog(true);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!selectedUserForReset || !newPassword) return;
    
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setIsResettingPassword(true);
    try {
      await axios.post(`${API}/users/${selectedUserForReset.id}/reset-password`, {
        new_password: newPassword
      });
      toast.success(`Password reset for ${selectedUserForReset.name}`);
      setShowResetPasswordDialog(false);
      setSelectedUserForReset(null);
      setNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setIsResettingPassword(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            User Management
          </h1>
          <p className="text-slate-500 mt-1">{users.length} users</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="bg-orange-500 hover:bg-orange-600 text-white btn-press" data-testid="add-user-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New User</DialogTitle>
              <DialogDescription>Add a new user to the CRM</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  placeholder="John Doe"
                  required
                  data-testid="user-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="john@locofast.com"
                  required
                  data-testid="user-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone *</Label>
                <Input
                  value={newUser.phone}
                  onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                  placeholder="+91 98765 43210"
                  required
                  data-testid="user-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="Minimum 6 characters"
                  minLength={6}
                  required
                  data-testid="user-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select
                  value={newUser.role}
                  onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                >
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Agent</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Parent Admin - Show only for agents */}
              {newUser.role !== 'admin' && newUser.role !== 'super_admin' && (
                <div className="space-y-2">
                  <Label>Parent Admin *</Label>
                  <Select
                    value={newUser.parent_admin_id || "_none_"}
                    onValueChange={(value) => setNewUser({ ...newUser, parent_admin_id: value === "_none_" ? "" : value })}
                  >
                    <SelectTrigger data-testid="user-parent-admin-select">
                      <SelectValue placeholder="Select parent admin" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none_">No parent admin</SelectItem>
                      {adminUsers.map((admin) => (
                        <SelectItem key={admin.id} value={admin.id}>
                          {admin.name} ({admin.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Joining Date */}
              <div className="space-y-2">
                <Label>Joining Date</Label>
                <Input
                  type="date"
                  value={newUser.joining_date}
                  onChange={(e) => setNewUser({ ...newUser, joining_date: e.target.value })}
                  data-testid="user-joining-date"
                />
              </div>

              {/* Fabric Category Multi-select */}
              <div className="space-y-2">
                <Label>Fabric Category</Label>
                <div className="flex gap-3 flex-wrap">
                  {["Woven", "Knits"].map((cat) => (
                    <label key={cat} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 p-1.5 rounded border border-slate-200 px-3">
                      <Checkbox
                        checked={newUser.fabric_categories.includes(cat)}
                        onCheckedChange={(checked) => {
                          setNewUser(prev => ({
                            ...prev,
                            fabric_categories: checked
                              ? [...prev.fabric_categories, cat]
                              : prev.fabric_categories.filter(c => c !== cat)
                          }));
                        }}
                      />
                      {cat}
                    </label>
                  ))}
                </div>
              </div>

              {/* Fabric Type Multi-select */}
              <div className="space-y-2">
                <Label>Fabric Type</Label>
                <div className="flex gap-3 flex-wrap">
                  {["Greige", "Dyed", "Printed", "Denim"].map((ft) => (
                    <label key={ft} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 p-1.5 rounded border border-slate-200 px-3">
                      <Checkbox
                        checked={newUser.fabric_types.includes(ft)}
                        onCheckedChange={(checked) => {
                          setNewUser(prev => ({
                            ...prev,
                            fabric_types: checked
                              ? [...prev.fabric_types, ft]
                              : prev.fabric_types.filter(f => f !== ft)
                          }));
                        }}
                      />
                      {ft}
                    </label>
                  ))}
                </div>
              </div>

              {/* Regions Multi-select */}
              <div className="space-y-2">
                <Label>Regions</Label>
                <div className="flex gap-3 flex-wrap">
                  {["India", "Bangladesh"].map((r) => (
                    <label key={r} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 p-1.5 rounded border border-slate-200 px-3">
                      <Checkbox
                        checked={newUser.regions.includes(r)}
                        onCheckedChange={(checked) => {
                          setNewUser(prev => ({
                            ...prev,
                            regions: checked
                              ? [...prev.regions, r]
                              : prev.regions.filter(x => x !== r)
                          }));
                        }}
                      />
                      {r}
                    </label>
                  ))}
                </div>
              </div>

              {/* CRM Type Selection */}
              <div className="space-y-2">
                <Label>CRM Type *</Label>
                <Select
                  value={newUser.crm_type}
                  onValueChange={(value) => setNewUser({ ...newUser, crm_type: value, export_countries: value === 'domestic' ? [] : newUser.export_countries })}
                >
                  <SelectTrigger data-testid="user-crm-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="domestic">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-orange-500" />
                        Domestic (India)
                      </div>
                    </SelectItem>
                    <SelectItem value="export">
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-blue-500" />
                        Export (International)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Export Countries - Only show when CRM type is Export */}
              {newUser.crm_type === 'export' && (
                <div className="space-y-3">
                  <Label>Assigned Countries *</Label>
                  <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border border-slate-200 rounded-lg p-3">
                    {EXPORT_COUNTRIES.map((country) => (
                      <label key={country} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 p-1 rounded">
                        <Checkbox
                          checked={newUser.export_countries.includes(country)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setNewUser({ ...newUser, export_countries: [...newUser.export_countries, country] });
                            } else {
                              setNewUser({ ...newUser, export_countries: newUser.export_countries.filter(c => c !== country) });
                            }
                          }}
                        />
                        {country}
                      </label>
                    ))}
                  </div>
                  {newUser.export_countries.length > 0 && (
                    <p className="text-xs text-slate-500">
                      Selected: {newUser.export_countries.join(', ')}
                    </p>
                  )}
                </div>
              )}
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={isSubmitting}
                  data-testid="submit-user-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : "Create User"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Users List + Targets */}
      <Tabs defaultValue="users" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
          <TabsTrigger value="targets" data-testid="tab-targets">Daily Targets</TabsTrigger>
        </TabsList>
        <TabsContent value="users">
          <Card className="border-slate-200">
            <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              No users found
            </div>
          ) : (
            <div className="divide-y divide-slate-200">
              {users.map((user) => (
                <div
                  key={user.id}
                  className="p-4 hover:bg-slate-50 transition-colors"
                  data-testid={`user-row-${user.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                      ['admin','super_admin'].includes(user.role) ? 'bg-purple-100' : 'bg-blue-100'
                    }`}>
                      {['admin','super_admin'].includes(user.role) ? (
                        <Shield className="w-5 h-5 text-purple-600" />
                      ) : (
                        <User className="w-5 h-5 text-blue-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-900">{user.name}</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          user.role === 'super_admin'
                            ? 'bg-rose-100 text-rose-700'
                            : ['admin','super_admin'].includes(user.role)
                              ? 'bg-purple-50 text-purple-700' 
                              : 'bg-blue-50 text-blue-700'
                        }`}>
                          {user.role === 'super_admin' ? 'Super Admin' : (user.role === 'admin' ? 'Admin' : 'Agent')}
                        </span>
                        {!user.is_active && (
                          <span className="bg-red-50 text-red-700 px-2 py-0.5 rounded text-xs font-medium">
                            Inactive
                          </span>
                        )}
                        {user.crm_type === 'export' ? (
                          <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1">
                            <Globe className="w-3 h-3" />
                            Export
                          </span>
                        ) : (
                          <span className="bg-orange-50 text-orange-700 px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            Domestic
                          </span>
                        )}
                        {user.id === currentUser?.id && (
                          <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded text-xs">
                            You
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {user.email}
                        </span>
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {user.phone}
                        </span>
                        {user.crm_type === 'export' && user.export_countries?.length > 0 && (
                          <span className="flex items-center gap-1 text-blue-600">
                            <Globe className="w-3 h-3" />
                            {user.export_countries.join(', ')}
                          </span>
                        )}
                        {user.parent_admin_id && !['admin','super_admin'].includes(user.role) && (
                          <span className="flex items-center gap-1 text-purple-600">
                            <Shield className="w-3 h-3" />
                            {adminUsers.find(a => a.id === user.parent_admin_id)?.name || 'Admin'}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 flex-wrap">
                      {/* Availability toggle - for round-robin lead assignment */}
                      <div className="flex items-center gap-2">
                        <span className={`text-sm ${user.is_available !== false ? 'text-emerald-600 font-medium' : 'text-slate-500'}`}>
                          {user.is_available !== false ? 'Available' : 'Off-duty'}
                        </span>
                        <Switch
                          checked={user.is_available !== false}
                          onCheckedChange={() => handleToggleAvailability(user.id, user.is_available !== false)}
                          className="data-[state=checked]:bg-emerald-500"
                          data-testid={`toggle-availability-${user.id}`}
                        />
                      </div>
                      {user.id !== currentUser?.id && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditDialog(user)}
                            className="text-slate-600 hover:text-blue-600"
                            data-testid={`edit-user-${user.id}`}
                          >
                            <Edit2 className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-slate-500">Active</span>
                            <Switch
                              checked={user.is_active}
                              onCheckedChange={() => handleToggleActive(user.id, user.is_active)}
                              data-testid={`toggle-active-${user.id}`}
                            />
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openResetPasswordDialog(user)}
                            className="text-slate-600 hover:text-blue-600"
                            data-testid={`reset-password-${user.id}`}
                          >
                            <KeyRound className="w-4 h-4 mr-1" />
                            Reset Password
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteUser(user.id)}
                            className="text-slate-400 hover:text-red-600"
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {user.id === currentUser?.id && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openResetPasswordDialog(user)}
                          className="text-slate-600 hover:text-blue-600"
                          data-testid="change-own-password"
                        >
                          <KeyRound className="w-4 h-4 mr-1" />
                          Change Password
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>
        <TabsContent value="targets">
          <AgentTargetsTab />
        </TabsContent>
      </Tabs>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Update user details, role, and admin mapping</DialogDescription>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={editingUser.name || ""}
                  onChange={(e) => setEditingUser(prev => ({ ...prev, name: e.target.value }))}
                  data-testid="edit-user-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={editingUser.phone || ""}
                  onChange={(e) => setEditingUser(prev => ({ ...prev, phone: e.target.value }))}
                  data-testid="edit-user-phone"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select
                    value={editingUser.role}
                    onValueChange={(value) => setEditingUser(prev => ({ ...prev, role: value }))}
                  >
                    <SelectTrigger data-testid="edit-user-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="user">Agent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>CRM Type</Label>
                  <Select
                    value={editingUser.crm_type || "domestic"}
                    onValueChange={(value) => setEditingUser(prev => ({ ...prev, crm_type: value }))}
                  >
                    <SelectTrigger data-testid="edit-user-crm-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="domestic">Domestic</SelectItem>
                      <SelectItem value="export">Export</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {editingUser.role !== 'admin' && (
                <div className="space-y-2">
                  <Label>Parent Admin (Reporting To)</Label>
                  <Select
                    value={editingUser.parent_admin_id || ""}
                    onValueChange={(value) => setEditingUser(prev => ({ ...prev, parent_admin_id: value }))}
                  >
                    <SelectTrigger data-testid="edit-user-parent-admin">
                      <SelectValue placeholder="Select admin" />
                    </SelectTrigger>
                    <SelectContent>
                      {adminUsers.map((admin) => (
                        <SelectItem key={admin.id} value={admin.id}>
                          {admin.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label>Joining Date</Label>
                <Input
                  type="date"
                  value={editingUser.joining_date || ""}
                  onChange={(e) => setEditingUser(prev => ({ ...prev, joining_date: e.target.value }))}
                  data-testid="edit-user-joining-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Fabric Category</Label>
                <div className="flex gap-3 flex-wrap">
                  {["Woven", "Knits"].map((cat) => (
                    <label key={cat} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 p-1.5 rounded border border-slate-200 px-3">
                      <Checkbox
                        checked={(editingUser.fabric_categories || []).includes(cat)}
                        onCheckedChange={(checked) => {
                          setEditingUser(prev => ({
                            ...prev,
                            fabric_categories: checked
                              ? [...(prev.fabric_categories || []), cat]
                              : (prev.fabric_categories || []).filter(c => c !== cat)
                          }));
                        }}
                      />
                      {cat}
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Fabric Type</Label>
                <div className="flex gap-3 flex-wrap">
                  {["Greige", "Dyed", "Printed", "Denim"].map((ft) => (
                    <label key={ft} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 p-1.5 rounded border border-slate-200 px-3">
                      <Checkbox
                        checked={(editingUser.fabric_types || []).includes(ft)}
                        onCheckedChange={(checked) => {
                          setEditingUser(prev => ({
                            ...prev,
                            fabric_types: checked
                              ? [...(prev.fabric_types || []), ft]
                              : (prev.fabric_types || []).filter(f => f !== ft)
                          }));
                        }}
                      />
                      {ft}
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Regions</Label>
                <div className="flex gap-3 flex-wrap">
                  {["India", "Bangladesh"].map((r) => (
                    <label key={r} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 p-1.5 rounded border border-slate-200 px-3">
                      <Checkbox
                        checked={(editingUser.regions || []).includes(r)}
                        onCheckedChange={(checked) => {
                          setEditingUser(prev => ({
                            ...prev,
                            regions: checked
                              ? [...(prev.regions || []), r]
                              : (prev.regions || []).filter(x => x !== r)
                          }));
                        }}
                      />
                      {r}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleEditUser} className="bg-blue-600 hover:bg-blue-700" data-testid="save-edit-user-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPasswordDialog} onOpenChange={setShowResetPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for {selectedUserForReset?.name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>New Password *</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimum 6 characters"
                required
                minLength={6}
                data-testid="new-password-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowResetPasswordDialog(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-blue-600 hover:bg-blue-700"
                disabled={isResettingPassword || !newPassword}
                data-testid="confirm-reset-password-btn"
              >
                {isResettingPassword ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Resetting...
                  </>
                ) : "Reset Password"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
